import React, { useContext, useState } from 'react';
import { Select, Space } from 'antd';

import VulnerabilitySummaryTab from '@totem/components/software/vulnerabilityReport/summary/VulnerabilitySummaryTab';
import { VulnerabilityReportInput } from '@totem/components/software/vulnerabilityReport/types';
import VulnerabilityReportContext from '@totem/components/software/vulnerabilityReport/vulnerabilityReportContext';
import VulnerabilityReportTable from '@totem/components/software/vulnerabilityReport/vulnerabilityReportTable';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';

const SEVERITY_OPTIONS = [
  { value: 'CRITICAL', label: 'Critical' },
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
  { value: 'NONE', label: 'Not scored' },
];

const KNOWN_EXPLOITED_OPTIONS = [
  { value: 'true', label: 'Known exploited' },
  { value: 'false', label: 'Not known exploited' },
];

const filterOption = (
  search: string,
  option?: { label: string; value: string },
) => (option?.label ?? '').toLowerCase().includes(search.toLowerCase());

// Filters a drill-down replaces; the filter bar's selections (region, building, severity, known exploited) are kept.
const DRILL_DOWN_RESET: Partial<VulnerabilityReportInput> = {
  packageName: undefined,
  packageVersion: undefined,
  packagePublisher: undefined,
  vulnerability: undefined,
  status: undefined,
  deviceId: undefined,
  controlSystemId: undefined,
};

const VulnerabilityReportLayout = () => {
  const { input, setInput } = useContext(VulnerabilityReportContext);
  const { regions, buildings } = useContext(OrganizationContext);
  const [activeTab, setActiveTab] = useState<string>('summary');

  const updateFilter = (update: Partial<VulnerabilityReportInput>) =>
    setInput({ ...update, page: 1 });

  const handleDrillDown = (update: Partial<VulnerabilityReportInput>) => {
    setInput({ ...DRILL_DOWN_RESET, ...update, page: 1 });

    setActiveTab('details');
  };

  const regionOptions = (regions ?? []).map((region) => ({
    value: region.id,
    label: region.name,
  }));
  // With regions selected, only offer those regions' buildings (a region's children are its buildings).
  const selectedRegionBuildings = new Set(
    (regions ?? [])
      .filter((region) => input.regionId?.includes(region.id))
      .flatMap((region) => (region.children ?? []).map((child) => child.id)),
  );
  const buildingOptions = (buildings ?? [])
    .filter(
      (building) =>
        !input.regionId?.length || selectedRegionBuildings.has(building.id),
    )
    .map((building) => ({ value: building.id, label: building.name }));

  return (
    <IBCard
      isCovered
      title="Software Vulnerability Report"
      extra={
        <Space wrap>
          <Select
            mode="multiple"
            style={{ minWidth: 160, maxWidth: 320 }}
            options={regionOptions}
            value={input.regionId ?? []}
            onChange={(values: string[]) => updateFilter({ regionId: values })}
            placeholder="All regions"
            showSearch
            filterOption={filterOption}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            style={{ minWidth: 180, maxWidth: 360 }}
            options={buildingOptions}
            value={input.buildingId ?? []}
            onChange={(values: string[]) =>
              updateFilter({ buildingId: values })
            }
            placeholder="All buildings"
            showSearch
            filterOption={filterOption}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            style={{ minWidth: 150, maxWidth: 300 }}
            options={SEVERITY_OPTIONS}
            value={input.severity ?? []}
            onChange={(values: string[]) => updateFilter({ severity: values })}
            placeholder="All severities"
            maxTagCount="responsive"
          />
          <Select
            allowClear
            style={{ minWidth: 170 }}
            options={KNOWN_EXPLOITED_OPTIONS}
            value={input.knownExploited?.[0]}
            onChange={(value?: string) =>
              updateFilter({ knownExploited: value ? [value] : undefined })
            }
            placeholder="Known exploited: any"
          />
        </Space>
      }
    >
      <IBTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'summary',
            label: 'Summary',
            children: <VulnerabilitySummaryTab onDrillDown={handleDrillDown} />,
          },
          {
            key: 'details',
            label: 'Details',
            children: <VulnerabilityReportTable />,
          },
        ]}
      />
    </IBCard>
  );
};

export default VulnerabilityReportLayout;
